import _ from 'lodash';
import Vue from 'vue';
import VueRouter from 'vue-router';
import { lsStorageService, lsStorageServiceConstants, lsTrackingService } from 'ls-vue-common';
import { GuestParentView, SignInView } from '@/views/Guest';
import {
    AddPropertyView,
    AuthorizedParentView,
    BillingView,
    CancelView,
    ChangeFrequencyView,
    ChangeProviderView,
    ChangeScopeView,
    ChangeServiceWindowView,
    ConversationView,
    CustomerSearchView,
    CustomerView,
    DisputeView,
    JobNotesView,
    OneTimeServiceDayChangeView,
    PastDisputesView,
    PauseView,
    PropertyView,
    ReactivateView,
    RecommendationView,
    RequiredServiceView,
    ScheduleView,
    ServiceNotesView,
    SkipView,
    UnpauseView,
} from '@/views/Authorized';
// eslint-disable-next-line import/no-cycle
import { apolloClient } from '@/vue-apollo';
import store from '@/store';
import { AUTH__JWT_FETCH } from '@/store/types';
import Me from '@/graphql/queries/Me.gql';

Vue.use(VueRouter);

const routes = [
    {
        path: '',
        name: 'guest',
        component: GuestParentView,
        children: [
            {
                path: '/login',
                name: 'login',
                component: SignInView,
            },
        ],
    },
    {
        path: '',
        name: 'authorized',
        component: AuthorizedParentView,
        meta: { requiresAuth: true },
        children: [
            {
                path: 'customer-search',
                name: 'customerSearch',
                component: CustomerSearchView,
                meta: { oldAdmin: 'customers' },
            },
            {
                path: 'customer/:customer_id/add-property',
                name: 'addProperty',
                component: AddPropertyView,
                meta: { oldAdmin: 'customers/:customer_id' },
            },
            {
                path: 'customer/:customer_id/',
                name: 'customer',
                component: CustomerView,
                meta: { oldAdmin: 'customers/:customer_id' },
                children: [
                    {
                        path: 'property/:property_id',
                        name: 'property',
                        component: PropertyView,
                        meta: { oldAdmin: 'customers/:customer_id' },
                        children: [
                            {
                                path: 'schedule/:schedule_id',
                                name: 'schedule',
                                component: ScheduleView,
                                meta: {
                                    oldAdmin:
                                        'customers/:customer_id/properties/:property_id/subscriptions/:schedule_id',
                                },
                                children: [
                                    {
                                        path: 'cancel',
                                        name: 'cancel',
                                        component: CancelView,
                                    },
                                    {
                                        path: 'change-frequency',
                                        name: 'changeFrequency',
                                        component: ChangeFrequencyView,
                                    },
                                    {
                                        path: 'change-provider',
                                        name: 'changeProvider',
                                        component: ChangeProviderView,
                                    },
                                    {
                                        path: 'change-scope',
                                        name: 'changeScope',
                                        component: ChangeScopeView,
                                    },
                                    {
                                        path: 'change-service-window',
                                        name: 'changeServiceWindow',
                                        component: ChangeServiceWindowView,
                                    },
                                    {
                                        path: 'conversation',
                                        name: 'viewConversation',
                                        component: ConversationView,
                                    },
                                    {
                                        path: 'dispute',
                                        name: 'dispute',
                                        component: DisputeView,
                                    },
                                    {
                                        path: 'job-notes',
                                        name: 'jobNotes',
                                        component: JobNotesView,
                                    },
                                    {
                                        path: 'one-time-service-day-change',
                                        name: 'oneTimeServiceDayChange',
                                        component: OneTimeServiceDayChangeView,
                                    },
                                    {
                                        path: 'past-disputes',
                                        name: 'pastDisputes',
                                        component: PastDisputesView,
                                    },
                                    {
                                        path: 'pause',
                                        name: 'pause',
                                        component: PauseView,
                                    },
                                    {
                                        path: 'reactivate',
                                        name: 'reactivate',
                                        component: ReactivateView,
                                    },
                                    {
                                        path: 'service-notes',
                                        name: 'serviceNotes',
                                        component: ServiceNotesView,
                                    },
                                    {
                                        path: 'skip',
                                        name: 'skip',
                                        component: SkipView,
                                    },
                                    {
                                        path: 'unpause',
                                        name: 'unpause',
                                        component: UnpauseView,
                                    },
                                ],
                            },
                            {
                                path: 'recommendation/:recommendation_id',
                                name: 'recommendation',
                                component: RecommendationView,
                                meta: {
                                    oldAdmin:
                                        'customers/:customer_id/properties/:property_id/recommendations/:recommendation_id',
                                },
                            },
                            {
                                path: 'required-service/:requiredService_id',
                                name: 'requiredService',
                                component: RequiredServiceView,
                                meta: {
                                    oldAdmin:
                                        'customers/:customer_id/properties/:property_id/requiredservice/:requiredService_id',
                                },
                            },
                        ],
                    },
                    {
                        path: 'billing',
                        name: 'billing',
                        component: BillingView,
                        meta: {
                            oldAdmin: 'customers/:customer_id',
                        },
                    },
                ],
            },
        ],
    },
];

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        } else {
            return { x: 0, y: 0 };
        }
    },
});

export function setOldAdminMeta({ to, authtoken }) {
    // get last parent route with oldAdmin meta param set
    const lastParent = _.findLast(to.matched, 'meta.oldAdmin');
    if (!_.get(lastParent, 'regex.keys')) {
        return null;
    }

    let { oldAdmin } = lastParent.meta;

    // loop through keys and replace them with route params
    _.forEach(lastParent.regex.keys, ({ name }) => {
        oldAdmin = _.replace(oldAdmin, `:${name}`, to.params[name]);
    });

    to.meta.oldAdminWithAuth = `${oldAdmin}?auth=${authtoken}`;
    to.meta.oldAdminWithOutAuth = `${oldAdmin}`;
}

export function beforeEach(to, from, next) {
    const { name, query } = to;
    // if an auth qp exists, save the token and clear out the url
    if (query.auth) {
        lsStorageService.set(lsStorageServiceConstants.AUTH_TOKEN, query.auth);

        apolloClient.query({
            query: Me,
        });

        store.dispatch(AUTH__JWT_FETCH);

        _.unset(to.query, 'auth');
        return router.replace(
            {
                name: to.name,
                params: to.params,
                query: to.query,
            },
            _.noop
        );
    }

    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
    const authtoken = lsStorageService.get(lsStorageServiceConstants.AUTH_TOKEN);

    setOldAdminMeta({ to, authtoken });

    // if this route requires auth
    if (requiresAuth) {
        // and they have a token and are logged in
        if (authtoken) {
            apolloClient
                .query({
                    query: Me,
                })
                .then(() => {
                    next();
                });
        } else {
            // otherwise, route to /login (attempting to access a protected route without a token)
            next({
                name: 'login',
            });
        }
    } else if (authtoken) {
        // else if route isn't protected, but they have a token
        // and they're logged in - re-route to /customerSearch
        apolloClient
            .query({
                query: Me,
            })
            .then(() => {
                next({ name: 'customerSearch' });
            });
    } else {
        // finally, if the route is unprotected and they have no token, proceed
        if (name === 'guest') {
            next({ name: 'login' });
        } else {
            next();
        }
    }
}

let referrer = '';
export function afterEach(to, from) {
    if (!from.name) {
        referrer = _.get(document, 'referrer');
    }
    lsTrackingService.page({ path: to.path, referrer });
}

router.beforeEach((to, from, next) => beforeEach(to, from, next));
router.afterEach((to, from) => afterEach(to, from));

export default router;
